import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountSecurity = ({ securityData, onUpdate }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(securityData?.twoFactorEnabled || false);

  const mockSecurityData = {
    twoFactorEnabled: false,
    lastPasswordChange: '2025-08-15',
    loginSessions: [
      {
        id: 1,
        device: 'Chrome on Windows',
        location: 'New York, NY',
        ipAddress: '192.168.1.100',
        lastActive: '2025-10-08T09:30:00Z',
        current: true
      },
      {
        id: 2,
        device: 'Safari on iPhone',
        location: 'New York, NY',
        ipAddress: '192.168.1.101',
        lastActive: '2025-10-07T18:45:00Z',
        current: false
      },
      {
        id: 3,
        device: 'Chrome on Android',
        location: 'Brooklyn, NY',
        ipAddress: '192.168.1.102',
        lastActive: '2025-10-06T14:20:00Z',
        current: false
      }
    ],
    securityEvents: [
      {
        id: 1,
        type: 'login',
        description: 'Successful login from Chrome on Windows',
        timestamp: '2025-10-08T09:30:00Z',
        status: 'success'
      },
      {
        id: 2,
        type: 'password_change',
        description: 'Password changed successfully',
        timestamp: '2025-08-15T16:22:00Z',
        status: 'success'
      },
      {
        id: 3,
        type: 'failed_login',
        description: 'Failed login attempt from unknown device',
        timestamp: '2025-08-10T12:15:00Z',
        status: 'warning'
      }
    ],
    privacySettings: {
      profileVisibility: 'members',
      showAchievements: true,
      showProgress: false,
      allowMessages: true,
      showOnlineStatus: true
    }
  };

  const data = securityData || mockSecurityData;

  const handlePasswordChange = () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (passwordData?.newPassword?.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }
    
    onUpdate?.({
      type: 'password',
      data: passwordData
    });
    
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordForm(false);
  };

  const handleTwoFactorToggle = () => {
    const newStatus = !twoFactorEnabled;
    setTwoFactorEnabled(newStatus);
    onUpdate?.({
      type: 'twoFactor',
      enabled: newStatus
    });
  };

  const handleTerminateSession = (sessionId) => {
    onUpdate?.({
      type: 'terminateSession',
      sessionId
    });
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'login': return 'LogIn';
      case 'password_change': return 'Key';
      case 'failed_login': return 'AlertTriangle';
      case 'two_factor': return 'Shield';
      default: return 'Activity';
    }
  };

  const getEventColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Shield" size={24} className="text-primary" />
        <h2 className="text-xl font-heading font-semibold text-foreground">Account Security</h2>
      </div>
      {/* Security Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center space-x-3 p-4 bg-success/10 rounded-lg">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <div>
            <p className="text-sm font-medium text-foreground">Account Verified</p>
            <p className="text-xs text-muted-foreground">Email confirmed</p>
          </div>
        </div>
        <div className={`flex items-center space-x-3 p-4 rounded-lg ${
          twoFactorEnabled ? 'bg-success/10' : 'bg-warning/10'
        }`}>
          <Icon name="Shield" size={20} className={twoFactorEnabled ? 'text-success' : 'text-warning'} />
          <div>
            <p className="text-sm font-medium text-foreground">Two-Factor Auth</p>
            <p className="text-xs text-muted-foreground">
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-4 bg-primary/10 rounded-lg">
          <Icon name="Key" size={20} className="text-primary" />
          <div>
            <p className="text-sm font-medium text-foreground">Password</p>
            <p className="text-xs text-muted-foreground">
              Changed {new Date(data.lastPasswordChange)?.toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
      {/* Password Management */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Password & Authentication</h3>
        
        {!showPasswordForm ? (
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={() => setShowPasswordForm(true)}
              iconName="Key"
              iconPosition="left"
            >
              Change Password
            </Button>
            <Button
              variant={twoFactorEnabled ? "destructive" : "default"}
              onClick={handleTwoFactorToggle}
              iconName="Shield"
              iconPosition="left"
            >
              {twoFactorEnabled ? 'Disable' : 'Enable'} Two-Factor Authentication
            </Button>
          </div>
        ) : (
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="space-y-4">
              <Input
                label="Current Password"
                type="password"
                value={passwordData?.currentPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e?.target?.value }))}
                required
              />
              <Input
                label="New Password"
                type="password"
                value={passwordData?.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e?.target?.value }))}
                description="Must be at least 8 characters long"
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={passwordData?.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                required
              />
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handlePasswordChange}
                  disabled={!passwordData?.currentPassword || !passwordData?.newPassword || !passwordData?.confirmPassword}
                  className="flex-1"
                >
                  Update Password
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Active Sessions */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Active Sessions</h3>
        <div className="space-y-3">
          {data?.loginSessions?.map((session) => (
            <div key={session?.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Monitor" size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">{session?.device}</h4>
                  <p className="text-sm text-muted-foreground">{session?.location}</p>
                  <p className="text-xs text-muted-foreground">
                    Last active: {new Date(session.lastActive)?.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {session?.current && (
                  <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                    Current Session
                  </span>
                )}
                {!session?.current && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTerminateSession(session?.id)}
                    iconName="X"
                  >
                    Terminate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Privacy Settings */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Privacy Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Profile Visibility
              </label>
              <select
                value={data?.privacySettings?.profileVisibility}
                onChange={(e) => onUpdate?.({
                  type: 'privacy',
                  setting: 'profileVisibility',
                  value: e?.target?.value
                })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="public">Public</option>
                <option value="members">Members Only</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <Checkbox
              label="Show Achievements"
              description="Display your badges and achievements on your profile"
              checked={data?.privacySettings?.showAchievements}
              onChange={(e) => onUpdate?.({
                type: 'privacy',
                setting: 'showAchievements',
                value: e?.target?.checked
              })}
            />
            <Checkbox
              label="Show Progress"
              description="Display your wellness progress to other members"
              checked={data?.privacySettings?.showProgress}
              onChange={(e) => onUpdate?.({
                type: 'privacy',
                setting: 'showProgress',
                value: e?.target?.checked
              })}
            />
            <Checkbox
              label="Allow Messages"
              description="Allow other members to send you direct messages"
              checked={data?.privacySettings?.allowMessages}
              onChange={(e) => onUpdate?.({
                type: 'privacy',
                setting: 'allowMessages',
                value: e?.target?.checked
              })}
            />
            <Checkbox
              label="Show Online Status"
              description="Show when you're online to other members"
              checked={data?.privacySettings?.showOnlineStatus}
              onChange={(e) => onUpdate?.({
                type: 'privacy',
                setting: 'showOnlineStatus',
                value: e?.target?.checked
              })}
            />
          </div>
        </div>
      </div>
      {/* Security Activity */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Recent Security Activity</h3>
        <div className="space-y-3">
          {data?.securityEvents?.slice(0, 5)?.map((event) => (
            <div key={event?.id} className="flex items-center space-x-4 p-3 bg-muted/50 rounded-lg">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                event?.status === 'success' ? 'bg-success/10' :
                event?.status === 'warning' ? 'bg-warning/10' : 'bg-error/10'
              }`}>
                <Icon 
                  name={getEventIcon(event?.type)} 
                  size={16} 
                  className={getEventColor(event?.status)} 
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{event?.description}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(event.timestamp)?.toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4">
          <Button variant="outline" size="sm">
            View Full Security Log
          </Button>
        </div>
      </div>
      {/* Emergency Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Emergency Actions</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            onClick={() => onUpdate?.({ type: 'logoutAll' })}
            iconName="LogOut"
            iconPosition="left"
          >
            Log Out All Devices
          </Button>
          <Button
            variant="destructive"
            onClick={() => onUpdate?.({ type: 'deactivateAccount' })}
            iconName="UserX"
            iconPosition="left"
          >
            Deactivate Account
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurity;